/**
 * EquipmentTable - Reusable table component for equipment databases
 * 
 * Based on the Flux Rate Database styling from AdminRates.js.
 * Provides a consistent look and feel for all equipment types.
 */

import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Button,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { Skeleton } from '../ux';

function EquipmentTable({
    title,
    description,
    columns = [],
    data = [],
    loading = false,
    onAdd,
    onEdit,
    onCopy,
    onDelete,
    onRefresh,
    emptyMessage = "No equipment entries yet.",
    filters = null
}) {
    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {onRefresh && (
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={onRefresh}
                            size="small"
                            sx={{ color: vortxColors.stone }}
                        >
                            Refresh
                        </Button>
                    )}
                    {onAdd && (
                        <Button
                            startIcon={<AddIcon />}
                            onClick={onAdd}
                            variant="outlined"
                            size="small"
                            className="vx-pressable"
                            sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                        >
                            Add Entry
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters */}
            {filters && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {filters}
                </Box>
            )}

            {/* Table */}
            {loading ? (
                <TableContainer component={Paper} sx={{ bgcolor: vortxColors.charcoal }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        sx={{ color: vortxColors.stone, width: col.width }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                {(onEdit || onCopy || onDelete) && (
                                    <TableCell align="right" sx={{ color: vortxColors.stone }}>
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    {columns.map((col) => (
                                        <TableCell key={col.id}>
                                            <Skeleton variant="text" className="vx-skeleton-text medium" />
                                        </TableCell>
                                    ))}
                                    {(onEdit || onCopy || onDelete) && (
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Skeleton variant="circular" width={28} height={28} />
                                                <Skeleton variant="circular" width={28} height={28} />
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : data.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: vortxColors.charcoal }}>
                    <Typography color="text.secondary">
                        {emptyMessage}
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} sx={{ bgcolor: vortxColors.charcoal }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        sx={{ color: vortxColors.stone, width: col.width }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                {(onEdit || onCopy || onDelete) && (
                                    <TableCell align="right" sx={{ color: vortxColors.stone }}>
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row, idx) => (
                                <TableRow
                                    key={row.id || idx}
                                    hover
                                    className="vx-slide-in-up vx-state-transition"
                                    sx={{
                                        cursor: onEdit ? 'pointer' : 'default',
                                        animationDelay: `${idx * 30}ms`,
                                    }}
                                >
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.id}
                                            sx={{
                                                color: col.highlight ? vortxColors.gold : vortxColors.white,
                                                fontWeight: col.highlight ? 600 : 400
                                            }}
                                        >
                                            {col.render ? col.render(row[col.id], row) : row[col.id] ?? '-'}
                                        </TableCell>
                                    ))}
                                    {(onEdit || onCopy || onDelete) && (
                                        <TableCell align="right">
                                            {onEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        onClick={() => onEdit(row)}
                                                        size="small"
                                                        sx={{ color: vortxColors.gold }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {onCopy && (
                                                <Tooltip title="Copy">
                                                    <IconButton
                                                        onClick={() => onCopy(row)}
                                                        size="small"
                                                        sx={{ color: vortxColors.teal }}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {onDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={() => onDelete(row)}
                                                        size="small"
                                                        sx={{ color: '#e74c3c' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Count chip */}
            {data.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                        label={`${data.length} entr${data.length !== 1 ? 'ies' : 'y'}`}
                        size="small"
                        sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
                    />
                </Box>
            )}
        </Box>
    );
}

export default EquipmentTable;
